// messageCenter/noticeWindows.js — the one owner of the notice window on a backend page (bigplan backend/message-center,
// step 8d; D-21): notices with display mode 'window' from the summary, one at a time, and a notice the person opened
// by hand on the inbox page ("Als Fenster öffnen", sent as a DOM event because the inbox page is another bundle).
//
// Closing a pending notice acknowledges it (read, acknowledged, login memory) and the counter follows; closing one
// opened by hand only forgets it. The shown notice — with title and text — and the window's state (position,
// minimised) are kept in sessionStorage and the window comes back on the next page without a request, so a tip stays
// open while the person works across pages. sessionStorage may be missing or full: then the window simply does not
// survive a page change (logged once, not fatal).
import { createNoticeWindow } from './noticeWindow.js';
import { announceInboxChange, OPEN_NOTICE_WINDOW, showingKey } from './inboxEvents.js';

export const WINDOW_STORAGE_KEY = 'mcNoticeWindow';

/** sessionStorage, or null where the browser refuses it (private mode, blocked storage). */
export function sessionStore(env = window) {
    try { return env.sessionStorage ?? null; } catch (e) { return null; }
}

export function createNoticeWindows({ api, doc = document, storage = sessionStore(), createWindow = createNoticeWindow }) {
    const queue = [];          // pending window notices not shown yet
    const known = new Set();   // showings of pending notices queued or shown on this page (showingKey: "show again" is new)
    let shown = null;          // { notice, manual }
    let storageFailed = false;

    const win = createWindow({ onClose: (notice) => closed(notice), onChange: (notice, state) => remember(notice, state) });
    const onOpen = (event) => openByHand(event.detail?.notice);
    doc.addEventListener(OPEN_NOTICE_WINDOW, onOpen);

    /** The window of the last page, if there was one. */
    function restore() {
        const kept = read();
        if (!kept?.notice?.thread_id) { return; }
        shown = { notice: kept.notice, manual: Boolean(kept.manual) };
        if (!shown.manual) { known.add(showingKey(kept.notice)); }
        win.show(kept.notice, kept.state ?? null);
    }

    /** Takes the pending notices of a summary; queues the windows not seen yet and shows the first one if none is open. */
    function showPending(pending = []) {
        for (const notice of pending) {
            if (notice.display !== 'window' || known.has(showingKey(notice))) { continue; }
            known.add(showingKey(notice));
            queue.push({ notice, manual: false });
        }
        if (!shown) { next(); }
    }

    /** Opened by hand from the inbox page: replaces what is shown; a pending one goes back to the front of the queue. */
    function openByHand(notice) {
        if (!notice?.thread_id) { return; }
        if (shown && !shown.manual && shown.notice.thread_id !== notice.thread_id) { queue.unshift(shown); }
        present({ notice: slim(notice), manual: true });
    }

    function next() {
        const item = queue.shift();
        if (!item) {
            shown = null;
            return;
        }
        present(item);
    }

    function present(item) {
        shown = item;
        win.show(item.notice);
        remember(item.notice, win.state());
    }

    function closed(notice) {
        const item = shown;
        forget();
        shown = null;
        if (item && !item.manual && notice) {
            api.acknowledgeNotice(notice.thread_id)
                .then((response) => {
                    if (!response?.success) { throw new Error(response?.error ?? 'no answer'); }
                    announceInboxChange(doc);
                })
                .catch((e) => console.error('message center: acknowledging the notice window failed', e));
        }
        next();
    }

    // ── sessionStorage ──

    function remember(notice, state) {
        if (!storage || !shown || !notice || shown.notice.thread_id !== notice.thread_id) { return; }
        write({ notice: shown.notice, manual: shown.manual, state });
    }

    function read() {
        if (!storage) { return null; }
        try { return JSON.parse(storage.getItem(WINDOW_STORAGE_KEY) || 'null'); } catch (e) { return null; }
    }

    function write(value) {
        try {
            storage.setItem(WINDOW_STORAGE_KEY, JSON.stringify(value));
        } catch (e) {
            if (!storageFailed) { console.error('message center: keeping the notice window failed', e); }
            storageFailed = true;
        }
    }

    function forget() {
        if (!storage) { return; }
        try { storage.removeItem(WINDOW_STORAGE_KEY); } catch (e) { /* nothing kept, nothing to forget */ }
    }

    function destroy() {
        doc.removeEventListener(OPEN_NOTICE_WINDOW, onOpen);
        win.destroy();
    }

    return { restore, showPending, openByHand, current: () => shown, destroy };
}

// only what the window needs on the next page
function slim(notice) {
    return { thread_id: notice.thread_id, title: notice.title, body: notice.body ?? '', display: notice.display,
             reshown_at: notice.reshown_at ?? null };
}
